import React, { useState } from 'react';
import { backupAPI } from '../../services/api';
import useIsMobile from '../../hooks/useIsMobile';

const BackupConfig = () => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [progress, setProgress] = useState({ percentage: 0, message: '', eta: '' });
  const [isCreating, setIsCreating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ percent: 0, loaded: 0, total: 0 });
  const [abortController, setAbortController] = useState(null);



  const handleCancelBackup = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setLoading(false);
      setIsCreating(false);
      setIsDownloading(false);
      setProgress({ percentage: 0, message: '', eta: '' });
      setDownloadProgress({ percent: 0, loaded: 0, total: 0 });
      setError('Backup cancelado por el usuario');
    }
  };

  const handleCreateBackup = async (type) => {
    setLoading(true);
    setIsCreating(true);
    setError(null);
    setSuccess(null);
    setProgress({ percentage: 0, message: 'Iniciando backup...', eta: 'Calculando...' });

    // Crear AbortController para poder cancelar
    const controller = new AbortController();
    setAbortController(controller);

    let progressInterval = null;

    try {
      // Simular progreso (en el futuro esto vendrá del WebSocket)
      progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev.percentage < 90) {
            const newPercentage = prev.percentage + Math.random() * 10;
            return {
              percentage: Math.min(newPercentage, 90),
              message: getProgressMessage(newPercentage, type),
              eta: calculateETA(newPercentage)
            };
          }
          return prev;
        });
      }, 500);

      // Crear backup
      const response = await backupAPI.createBackup(type, controller.signal);
      
      clearInterval(progressInterval);
      setProgress({ percentage: 100, message: 'Backup creado exitosamente', eta: '' });
      
      // Descargar archivo directamente
      await downloadBackup(response.data.backup.id, type, controller.signal);

      setSuccess(`Descarga completada exitosamente`);
    } catch (error) {
      if (progressInterval) clearInterval(progressInterval);
      
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        setError('Backup cancelado');
      } else {
        setError('Error al crear o descargar el backup: ' + error.message);
      }
    } finally {
      if (progressInterval) clearInterval(progressInterval);
      setLoading(false);
      setIsCreating(false);
      setProgress({ percentage: 0, message: '', eta: '' });
      setAbortController(null);
    }
  };

  const downloadBackup = async (backupId, type, signal) => {
    try {
      setIsDownloading(true);
      setDownloadProgress({ percent: 0, loaded: 0, total: 0 });
      
      // Descargar con progreso real
      const response = await backupAPI.downloadBackup(
        backupId, 
        (progressData) => {
          setDownloadProgress(progressData);
        },
        signal
      );
      
      // Método clásico de descarga (compatible con todos los navegadores)
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const extension = type === 'database' ? 'sql' : 'zip';
      link.setAttribute('download', `backup_${type}_${new Date().toISOString().split('T')[0]}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccess('Archivo descargado exitosamente a tu carpeta de Descargas');
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        throw new Error('Descarga cancelada');
      }
      throw new Error('Error al descargar el archivo: ' + error.message);
    } finally {
      setIsDownloading(false);
      setDownloadProgress({ percent: 0, loaded: 0, total: 0 });
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getProgressMessage = (percentage, type) => {
    if (percentage < 20) return 'Preparando archivos...';
    if (percentage < 40) return type === 'database' ? 'Exportando base de datos...' : 'Comprimiendo datos...';
    if (percentage < 60) return type === 'database' ? 'Generando archivo SQL...' : 'Creando archivo ZIP...';
    if (percentage < 80) return 'Finalizando backup...';
    if (percentage < 100) return 'Preparando descarga...';
    return 'Completado';
  };

  const calculateETA = (percentage) => {
    if (percentage === 0) return 'Calculando...';
    if (percentage >= 100) return '';
    
    const remaining = 100 - percentage;
    const estimatedSeconds = Math.round((remaining / percentage) * 30); // Estimación simple
    
    if (estimatedSeconds < 60) return `${estimatedSeconds}s restantes`;
    const minutes = Math.round(estimatedSeconds / 60);
    return `${minutes}m restantes`;
  };


  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ 
          color: '#2c3e50', 
          marginBottom: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          💾 Descarga de Datos
        </h2>
        <p style={{ color: '#7f8c8d', margin: 0 }}>
          Descarga datos directamente a tu PC
        </p>
      </div>

      {/* Alertas */}
      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          color: '#721c24',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <strong>❌ Error:</strong> {error}
        </div>
      )}

      {success && (
        <div style={{
          backgroundColor: '#d4edda',
          border: '1px solid #c3e6cb',
          color: '#155724',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <strong>✅ Éxito:</strong> {success}
        </div>
      )}

      {/* Barra de progreso - CREACIÓN */}
      {isCreating && (
        <div style={{
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <div style={{ marginBottom: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
              <span style={{ fontWeight: '500', color: '#495057' }}>
                <i className="fas fa-compress-alt" style={{ marginRight: '8px' }}></i>
                {progress.message}
              </span>
              <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>{progress.eta}</span>
            </div>
            <div style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#e9ecef',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${progress.percentage}%`,
                height: '100%',
                backgroundColor: '#28a745',
                transition: 'width 0.3s ease',
                borderRadius: '4px'
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
              <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                {Math.round(progress.percentage)}%
              </div>
              <button
                onClick={handleCancelBackup}
                style={{
                  padding: '6px 16px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <i className="fas fa-times"></i>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Barra de progreso - DESCARGA */}
      {isDownloading && (
        <div style={{
          backgroundColor: '#e3f2fd',
          border: '1px solid #2196f3',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <div style={{ marginBottom: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
              <span style={{ fontWeight: '500', color: '#1976d2' }}>
                <i className="fas fa-download" style={{ marginRight: '8px' }}></i>
                Descargando archivo...
              </span>
              <span style={{ color: '#1976d2', fontSize: '0.9rem' }}>
                {formatBytes(downloadProgress.loaded)} / {formatBytes(downloadProgress.total)}
              </span>
            </div>
            <div style={{
              width: '100%',
              height: '10px',
              backgroundColor: '#bbdefb',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${downloadProgress.percent}%`,
                height: '100%',
                backgroundColor: '#2196f3',
                transition: 'width 0.3s ease',
                borderRadius: '4px'
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
              <div style={{ color: '#1976d2', fontSize: '0.9rem', fontWeight: '600' }}>
                {downloadProgress.percent}%
              </div>
              <button
                onClick={handleCancelBackup}
                style={{
                  padding: '6px 16px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <i className="fas fa-times"></i>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Botones de descarga */}
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: isMobile ? '1.1rem' : '1.5rem' }}>Tipos de Descarga</h3>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: '15px' 
        }}>
          {[
            { type: 'database', label: 'Base de Datos', icon: '🗄️', color: '#3498db' },
            { type: 'expedientes', label: 'Expedientes', icon: '📁', color: '#e74c3c' },
            { type: 'versiones', label: 'Versiones', icon: '📄', color: '#f39c12' },
            { type: 'complete', label: 'Copia Completa', icon: '💾', color: '#9b59b6' }
          ].map(({ type, label, icon, color }) => (
            <button
              key={type}
              onClick={() => handleCreateBackup(type)}
              disabled={loading}
              style={{
                padding: '20px',
                border: `2px solid ${color}`,
                borderRadius: '12px',
                backgroundColor: loading ? '#f8f9fa' : 'white',
                color: loading ? '#6c757d' : '#2c3e50',
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                textAlign: 'center',
                opacity: loading ? 0.6 : 1
              }}
              onMouseOver={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 20px rgba(0,0,0,0.15)';
                }
              }}
              onMouseOut={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                }
              }}
            >
              <div style={{ fontSize: '2rem', marginBottom: '10px' }}>{icon}</div>
              <div style={{ fontWeight: '600', marginBottom: '5px' }}>{label}</div>
              <div style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>
                {type === 'database' && 'Archivo SQL'}
                {type === 'expedientes' && 'Carpetas EXP'}
                {type === 'versiones' && 'Archivos de versiones'}
                {type === 'complete' && 'Todo completo'}
              </div>
            </button>
          ))}
        </div>
      </div>


    </div>
  );
};

export default BackupConfig;
